import axios from 'axios';

const STRAICO_API_URL = import.meta.env.VITE_STRAICO_API_URL || 'https://api.straico.com/v1';
const STRAICO_API_KEY = import.meta.env.VITE_STRAICO_API_KEY;

// Create axios instance with default config
export const straicoClient = axios.create({
  baseURL: STRAICO_API_URL,
  timeout: 60000, // 60 seconds timeout for AI generation
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${STRAICO_API_KEY}`
  }
});

// Updated model mapping for Straico API
export const MODEL_MAPPING = {
  'gpt-4': 'openai/gpt-4o-mini',
  'claude-3': 'anthropic/claude-3.5-sonnet'
};

const buildPrompt = (formData) => {
  const { inputMode, jobTitle, jobDescription, salaryRange, location, seniority, workMode, notes } = formData;
  
  let prompt = `You are a professional HR content writer specializing in creating comprehensive job postings for municipal positions. Based on the information provided in the job description or title, generate a complete and detailed job posting for a position at The City of Metropolis.

Requirements for your output:

Context:
- The position is with The City of Metropolis, serving local government and community needs.
- The job posting will be used on the official city website and shared with employee services professionals and potential candidates.
- Accuracy and professionalism are critical, as this will represent the city publicly.

Output Structure:
A. Job Title
B. Department
C. Location (City of Metropolis)
D. Position Summary (2-3 sentences summarizing the main purpose of the role)
E. Key Responsibilities (bullet-point list; 5-10 items, tailored to the job description)
F. Required Qualifications (education, experience, certifications, skills—bullet list)
G. Preferred Qualifications (if applicable—bullet list)
H. Required Competencies (soft skills, values, or attributes; 3-5 bullet points)
I. Salary Range (if provided; else state "Commensurate with experience")
J. Application Instructions (how to apply, deadline if applicable)
K. Equal Opportunity Statement (standard wording)
L. Contact Information (email and/or phone for HR)

Constraints and Limitations:
- Use professional, concise, and unbiased language.
- Avoid any discriminatory or non-inclusive wording.
- Limit total output to a maximum of 4096 tokens.
- Do not invent details: only use information given in the job description, or use standard language/placeholders if details are missing.

Examples:
Example 1: (Assume the following input job description: "Job Title: Waste Management Supervisor. Oversee daily recycling and trash operations. Requires 3+ years experience, supervisory skills, valid driver's license. Bilingual ability preferred.")

Job Title: Waste Management Supervisor
Department: Department of Public Works
Location: City of Metropolis

Position Summary:
The Waste Management Supervisor oversees daily operations relating to recycling and trash collection, ensuring high service standards and regulatory compliance across the City of Metropolis. The position leads a team to promote sustainable waste disposal solutions for the community.

Key Responsibilities:
- Supervise day-to-day operations of waste collection and recycling services
- Train, schedule, and evaluate waste management staff
- Ensure compliance with municipal and environmental regulations
- Address service issues and customer concerns promptly
- Prepare operational and incident reports
- Implement safety protocols for vehicles and staff

Required Qualifications:
- Minimum 3 years of experience in waste management or related field
- Prior supervisory experience required
- Valid driver's license
- Strong organizational and communication skills

Preferred Qualifications:
- Bilingual abilities (English/Spanish)

Required Competencies:
- Leadership and team motivation
- Commitment to public service
- Attention to detail
- Problem-solving skills

Salary Range: Commensurate with experience

Application Instructions:
Submit your application (resume and cover letter) via the City of Metropolis careers portal by [deadline].

Equal Opportunity Statement:
The City of Metropolis is an equal opportunity employer and values diversity in its workforce.

Contact Information:
Employee Services Department, hr@metropolis.gov | (555) 123-4567

---

Example 2: (Assume the following input job description: "Job Title: City Planner. Develop city plans, review zoning permits, public speaking required. Master's degree preferred, 2+ years experience.")

Job Title: City Planner
Department: Planning and Development
Location: City of Metropolis

Position Summary:
The City Planner develops urban plans and reviews zoning permits to guide the strategic growth and development of the City of Metropolis. This role involves public presentations, stakeholder engagement, and application of planning regulations.

Key Responsibilities:
- Develop and update city development plans and zoning ordinances
- Review and process zoning and land use permit applications
- Conduct public hearings and information sessions
- Collaborate with city officials, developers, and the public
- Prepare reports and recommendations for planning committees

Required Qualifications:
- Bachelor's degree in Urban Planning or related field
- Minimum 2 years of experience in city planning or a relevant position
- Strong written and oral communication skills
- Proficiency in GIS and planning software

Preferred Qualifications:
- Master's degree in Urban Planning

Required Competencies:
- Public speaking and presentation skills
- Analytical thinking
- Collaborative approach
- Ethical judgment

Salary Range: Commensurate with experience

Application Instructions:
Apply online at the City of Metropolis careers site. Applications accepted until [deadline].

Equal Opportunity Statement:
The City of Metropolis is an equal opportunity employer and values diversity in its workforce.

Contact Information:
Planning Department HR, planning.hr@metropolis.gov | (555) 765-4321

---`;

  if (inputMode === 'title') {
    prompt += `Job Title: ${jobTitle}\n\n`;
    prompt += `Please create a comprehensive job posting including:\n`;
    prompt += `- A compelling job summary\n`;
    prompt += `- Key responsibilities\n`;
    prompt += `- Required qualifications and skills\n`;
    prompt += `- Preferred qualifications\n`;
    prompt += `- Company culture highlights\n`;
  } else {
    prompt += `Existing Job Description:\n${jobDescription}\n\n`;
    prompt += `Please improve and reformat this job description to be more engaging and professional.\n`;
  }

  // Add optional details
  if (salaryRange) {
    prompt += `\nSalary Range: ${salaryRange}`;
  }
  if (location) {
    prompt += `\nLocation: ${location}`;
  }
  if (seniority) {
    prompt += `\nSeniority Level: ${seniority}`;
  }
  if (workMode) {
    prompt += `\nWork Mode: ${workMode}`;
  }
  if (notes) {
    prompt += `\nAdditional Notes: ${notes}`;
  }

  prompt += `\n\nPlease format the job posting in a clear, professional manner that would be attractive to potential candidates. Use proper headings and bullet points where appropriate. Make it engaging and highlight the key benefits of the role.`;

  return prompt;
};

export const generateJobPosting = async (formData) => {
  if (!STRAICO_API_KEY) {
    throw new Error('Straico API key is not configured. Please check your environment variables.');
  }

  try {
    const prompt = buildPrompt(formData);
    const model = MODEL_MAPPING[formData.selectedModel] || 'openai/gpt-4o-mini';

    console.log('Making Straico API request with:', {
      model,
      promptLength: prompt.length,
      endpoint: `${STRAICO_API_URL}/prompt/completion`
    });

    // Try the exact format from Straico documentation
    const requestBody = {
      model: model, // Single model instead of array
      message: prompt,
      temperature: 0.7,
      max_tokens: 2000
    };

    console.log('Request body:', JSON.stringify(requestBody, null, 2));

    const response = await straicoClient.post('/prompt/completion', requestBody);
    
    console.log('Straico API Response:', response.data);

    // Handle different possible response structures
    if (response.data && response.data.completion) {
      // Direct completion in response
      return {
        jobPosting: response.data.completion.trim(),
        model: model,
        usage: response.data.usage || {},
        price: response.data.price || null
      };
    } else if (response.data && response.data.data) {
      // Nested data structure
      const responseData = response.data.data;
      if (responseData.completions) {
        const completions = responseData.completions;
        const modelKey = Object.keys(completions)[0];
        const completion = completions[modelKey];
        if (completion && completion.completion && completion.completion.choices) {
          const content = completion.completion.choices[0].message.content;
          return {
            jobPosting: content.trim(),
            model: model,
            usage: completion.completion.usage || {},
            price: completion.price || null
          };
        }
      }
    } else if (response.data && response.data.choices) {
      // OpenAI-like format
      const content = response.data.choices[0].message.content;
      return {
        jobPosting: content.trim(),
        model: model,
        usage: response.data.usage || {},
        price: null
      };
    }

    // If we can't parse the response, throw an error
    throw new Error('Unable to parse Straico API response structure');

  } catch (error) {
    console.error('Straico API Error Details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        data: error.config?.data
      }
    });

    if (error.response) {
      // API responded with error status
      const status = error.response.status;
      const errorData = error.response.data;

      let errorMessage = 'Unknown API error';
      if (errorData && typeof errorData === 'object') {
        if (errorData.error && errorData.error.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.response.statusText) {
        errorMessage = error.response.statusText;
      }

      switch (status) {
        case 400:
          throw new Error(`Bad Request: ${errorMessage}. Please check your input parameters.`);
        case 401:
          throw new Error('Invalid API key. Please check your Straico API configuration.');
        case 422:
          throw new Error(`Request validation failed: ${errorMessage}. The request format may be incorrect.`);
        case 429:
          throw new Error('Rate limit exceeded. Please try again in a few minutes.');
        case 500:
          throw new Error('Straico API is temporarily unavailable. Please try again later.');
        case 503:
          throw new Error('Straico API service is temporarily unavailable. Please try again later.');
        default:
          throw new Error(`API Error (${status}): ${errorMessage}`);
      }
    } else if (error.request) {
      // Network error
      throw new Error('Network error. Please check your internet connection and try again.');
    } else if (error.code === 'ENOTFOUND') {
      throw new Error('Cannot reach Straico API. Please check the API URL configuration.');
    } else {
      // Other error
      throw new Error(error.message || 'An unexpected error occurred while generating the job posting.');
    }
  }
};

// Alternative request format to try if the first one fails
export const generateJobPostingAlternative = async (formData) => {
  if (!STRAICO_API_KEY) {
    throw new Error('Straico API key is not configured. Please check your environment variables.');
  }

  try {
    const prompt = buildPrompt(formData);
    const model = MODEL_MAPPING[formData.selectedModel] || 'openai/gpt-4o-mini';

    // Try with models array format (as in your original example)
    const requestBody = {
      models: [model],
      message: prompt
    };

    console.log('Trying alternative format:', JSON.stringify(requestBody, null, 2));

    const response = await straicoClient.post('/prompt/completion', requestBody);

    // Process response similar to above
    if (response.data && response.data.data && response.data.data.completions) {
      const completions = response.data.data.completions;
      const modelKey = Object.keys(completions)[0];
      const completion = completions[modelKey];
      if (completion && completion.completion && completion.completion.choices) {
        const content = completion.completion.choices[0].message.content;
        return {
          jobPosting: content.trim(),
          model: model,
          usage: completion.completion.usage || {},
          price: completion.price || null
        };
      }
    }

    throw new Error('Unable to parse alternative API response');
  } catch (error) {
    throw error;
  }
};

// Mock job posting generator for development/fallback
const generateMockJobPosting = (formData) => {
  const { inputMode, jobTitle, jobDescription, salaryRange, location, seniority, workMode, notes } = formData;
  
  const title = inputMode === 'title' ? jobTitle : 'Software Engineer';
  const company = 'TechCorp Inc.';

  return `# ${title}

**Company:** ${company}
${location ? `**Location:** ${location}` : ''}
${salaryRange ? `**Salary:** ${salaryRange}` : ''}
${workMode ? `**Work Mode:** ${workMode.charAt(0).toUpperCase() + workMode.slice(1)}` : ''}

## About the Role

We are seeking a talented ${title} to join our dynamic team. This is an exciting opportunity to work on cutting-edge projects and make a significant impact on our products and services.

## Key Responsibilities

• Develop and maintain high-quality software applications
• Collaborate with cross-functional teams to deliver exceptional products
• Participate in code reviews and maintain coding standards
• Contribute to technical architecture and design decisions
• Mentor junior team members and share knowledge

## Required Qualifications

• Bachelor's degree in Computer Science or related field
• ${seniority === 'entry' ? '1-2' : seniority === 'mid' ? '3-5' : seniority === 'senior' ? '5+' : '3+'} years of professional development experience
• Strong problem-solving skills and attention to detail
• Excellent communication and teamwork abilities
• Experience with modern development frameworks and tools

## What We Offer

• Competitive salary and comprehensive benefits package
• Flexible working arrangements and work-life balance
• Professional development opportunities and career growth
• Collaborative and inclusive work environment
• Latest technology and tools

${notes ? `\n## Additional Information\n\n${notes}` : ''}

## How to Apply

Interested candidates should submit their resume and cover letter. We are an equal opportunity employer committed to diversity and inclusion.

---
*Generated using AI Job Posting Generator - ${new Date().toLocaleDateString()}*`;
};

export default { generateJobPosting, generateJobPostingAlternative };